import React, { useContext } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import {
  RecentActors as RecentActorsIcon,
  Person as PersonIcon,
  Today as TodayIcon,
  PersonOutlined as PersonOutlinedIcon,
} from '@mui/icons-material';
import CardBase from 'components/CardBase';
import FileUpload from 'components/FileUpload';
import NationalService from 'services/national';
import PersonService from 'services/person';
import CreateProcessContext from 'shared/contexts/CreateProcessContext';
import { resolvers } from 'shared/enums';
import { FileData, FileType } from 'API';
import IconRow from './IconRow';
import IconRowCell from './IconRowCell';
import S3Avatar from './S3Avatar';
import CardHeader from './CardHeader';

const { fullname } = PersonService;
const { formatImmigrationStatus } = NationalService;


interface Props {
  showFileUpload?: boolean;
  showImmigrationStatus?: boolean;
}

const CandidateCard: React.FC<Props> = (
  { showFileUpload, showImmigrationStatus }: Props,
) => {
  const {
    process, setProcess, setResume, selectedNational,
  } = useContext(CreateProcessContext);

  const data = process.foreignNationalID
    ? selectedNational
    : process.employeeData;

  const handleResumeSave = (file: FileData) => {
    setProcess((draft) => {
      draft.resumeID = file.id;
    });
    setResume(file);
  };

  const handleResumeCancel = () => {
    setProcess((draft) => {
      draft.resumeID = null;
    });
    setResume(undefined);
  };

  return (
    <CardBase>
      <CardHeader title="Individual Information" icon={PersonOutlinedIcon} />
      <Box sx={{ display: 'flex' }}>
        <S3Avatar
          alt={`${fullname(selectedNational || process.employeeData)} avatar`}
          picture={selectedNational?.avatar}
          className="avatar--shadow"
          sx={(t) => ({
            height: '4rem',
            marginRight: '1rem',
            width: '4rem',
            [t.breakpoints.down('sm')]: { width: '2.5rem', height: '2.5rem' },
          })}
        />
        <Box sx={{ flexGrow: 1 }}>
          <Box
            sx={(t) => ({
              mb: 2,
              [t.breakpoints.down('sm')]: { '& .MuiTypography-root': { fontSize: '1rem' } },
            })}
          >
            {process.foreignNationalID
              ? selectedNational && (
                <>
                  <Typography>{fullname(selectedNational)}</Typography>
                  {selectedNational.immigrationStatus && (
                    <Typography>
                      {formatImmigrationStatus(selectedNational)}
                    </Typography>
                  )}
                </>
              )
              : process?.employeeData && (
                <Typography>{fullname(process.employeeData)}</Typography>
              )}
            {data?.email && (
              <Typography variant="body1">{data?.email}</Typography>
            )}
          </Box>

          <Grid container>
            <Grid
              size={{
                sm: 12,
                md: 6
              }}>
              <IconRow size="small">
                <IconRowCell type="icon">
                  <PersonIcon color="action" />
                </IconRowCell>
                <IconRowCell type="text">
                  <Typography variant="caption">
                    Spouse:
                    {' '}
                    <strong>
                      {data?.spouse && 'Yes'}
                      {data?.spouse === false && 'No'}
                      {data?.spouse == null && 'N/A'}
                    </strong>
                  </Typography>
                </IconRowCell>
              </IconRow>
            </Grid>
            <Grid
              size={{
                sm: 12,
                md: 6
              }}>
              <IconRow size="small">
                <IconRowCell type="icon">
                  <PersonIcon color="action" />
                </IconRowCell>
                <IconRowCell type="text">
                  <Typography variant="caption">
                    Dependents:
                    {' '}
                    <strong>{data?.dependents || 'N/A'}</strong>
                  </Typography>
                </IconRowCell>
              </IconRow>
            </Grid>
          </Grid>

          {process.foreignNationalID && (
            <IconRow size="small">
              <IconRowCell type="icon">
                <RecentActorsIcon color="action" />
              </IconRowCell>
              <IconRowCell type="text">
                <Typography variant="caption">
                  Current immigration status:
                  {' '}
                  <strong>
                    {selectedNational?.immigrationStatus?.status || 'N/A'}
                  </strong>
                </Typography>
              </IconRowCell>
            </IconRow>
          )}

          {process.foreignNationalID && (
            <IconRow size="small">
              <IconRowCell type="icon">
                <TodayIcon color="action" />
              </IconRowCell>
              <IconRowCell type="text">
                <Typography variant="caption">
                  Expiration date:
                  {' '}
                  <strong>
                    {selectedNational?.immigrationStatus?.expiration || 'N/A'}
                  </strong>
                </Typography>
              </IconRowCell>
            </IconRow>
          )}

          {showImmigrationStatus && (
            <IconRow size="small">
              <IconRowCell type="icon">
                <RecentActorsIcon color="action" />
              </IconRowCell>
              <IconRowCell type="text">
                <Typography variant="caption">
                  Requested immigration process:
                  {' '}
                  <strong>
                    {process.requestedProcess
                      ? resolvers.VisaStatus(process.requestedProcess)
                      : 'N/A'}
                  </strong>
                </Typography>
              </IconRowCell>
            </IconRow>
          )}

          {showFileUpload && (
            <>
              <Divider />
              <FileUpload
                label="Resume"
                onSave={handleResumeSave}
                onCancel={handleResumeCancel}
                ownership={{
                  foreignNationalID: process?.foreignNationalID
                    || process.employeeData?.tempID || undefined,
                }}
                fileType={FileType.RESUME}
                storage={{
                  ownerNationalID: process?.foreignNationalID
                    || process.employeeData?.tempID || '',
                }}
              />
            </>
          )}
        </Box>
      </Box>
    </CardBase>
  );
};

export default CandidateCard;
